import { ShippingInfo } from "../../Interfaces/ShippingInfo";
import { usePopup } from "../Popup/PopupContext";
import PopupModel from "../Popup/PopupModel";
import { useState } from "react";
import { stateAbbreviations } from "../../CommonFunctions";
import apiClient from "../../services/apiClient";
import config from "../../config";
import {
  handleShippingErrorPopup,
  handleShippingWarningPopup,
} from "./ShippingErrors";

const BASE_API = config.API_BASE_URL;

interface ShippingAddressFormProps {
  setShippingInfo: React.Dispatch<React.SetStateAction<ShippingInfo>>;
  shippingInfo: ShippingInfo;
}

const validateShippingInfo = async (
  shippingInfo: ShippingInfo,
  setShippingInfo: (info: ShippingInfo) => void,
  showPopup: (
    header: string,
    body: React.ReactNode,
    buttons: { label: string; onClick: () => void }[],
    width?: string
  ) => void,
  hidePopup: () => void
) => {
  const newErrors: string[] = [];
  const shippingWarnings: string[] = [];

  const parts = shippingInfo.ship_to_name.trim().split(/\s+/);
  let first_name = parts[0] || "";
  let last_name = parts.length > 1 ? parts.slice(1).join(" ") : parts[0] || "";
  let foundAddress: ShippingInfo;

  try {
    const response = await apiClient.post(
      `${BASE_API}checkout/check-shipping-info/`,
      {
        formData: {
          first_name,
          last_name,
          street_address: shippingInfo.street_address,
          apt_suite: shippingInfo.apt_suite || "",
          city: shippingInfo.city,
          state: shippingInfo.state,
          zip_code: shippingInfo.zip_code,
        },
      }
    );

    const messages = response.data?.message ?? [];
    const address = response.data.address;

    foundAddress = {
      ...address,
      ship_to_name: shippingInfo.ship_to_name,
    };

    if (response.data?.is_valid === false) {
      newErrors.push(...messages);
    } else if (messages.length > 0) {
      shippingWarnings.push(...messages);
    }
    if (shippingWarnings.length > 0) {
      handleShippingWarningPopup(
        shippingInfo,
        shippingWarnings,
        foundAddress,
        setShippingInfo,
        showPopup,
        hidePopup
      );
      return false;
    }

    if (newErrors.length > 0) {
      handleShippingErrorPopup(shippingInfo, newErrors, showPopup);
      return false;
    }

    return true;
  } catch (err) {
    newErrors.push("Something went wrong validating your address.");
    return false;
  }
};

const getFormattedInputAddress = (shippingInfo: ShippingInfo) => {
  return (
    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
      <div>{shippingInfo.street_address}</div>
      {shippingInfo.apt_suite && <div>{shippingInfo.apt_suite}</div>}
      <div>
        {shippingInfo.city}, {shippingInfo.state} {shippingInfo.zip_code}
      </div>
    </div>
  );
};

const ShippingAddressForm: React.FC<ShippingAddressFormProps> = ({
  setShippingInfo,
  shippingInfo,
}) => {
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setShippingInfo({
      ...shippingInfo,
      [name]: value,
    });
  };

  return (
    <div
      style={{
        position: "relative",
      }}
    >
      <form
        autoComplete="off"
        style={{
          ...styles.formContainer,
          filter: "none",
          pointerEvents: "auto",
        }}
      >
        <div style={styles.shippingBox}>
          <div style={styles.shippingHeader}>Shipping</div>
          <div className="checkout-page-shipping-form-group row">
            <div className="checkout-page-shipping-form-col">
              <input
                type="text"
                id="ship_to_name"
                name="ship_to_name"
                value={shippingInfo.ship_to_name}
                onChange={handleChange}
                style={styles.inputField}
                placeholder="Full Name"
                required
              />
            </div>
          </div>
          <div className="checkout-page-shipping-form-group row">
            <div
              className="checkout-page-shipping-form-col"
              style={{ flex: "0 0 66%" }}
            >
              <input
                type="text"
                id="street_address"
                name="street_address"
                value={shippingInfo.street_address}
                onChange={handleChange}
                style={styles.inputField}
                placeholder="Address"
                required
              />
            </div>
            <div
              className="checkout-page-shipping-form-col"
              style={{ flex: "0 0 30%" }}
            >
              <input
                type="text"
                id="apt_suite"
                name="apt_suite"
                value={shippingInfo.apt_suite}
                onChange={handleChange}
                style={styles.inputField}
                placeholder="Apt # / Suite"
              />
            </div>
          </div>
          <div className="checkout-page-shipping-form-group row">
            <div className="checkout-page-shipping-form-col">
              <input
                type="text"
                id="city"
                name="city"
                value={shippingInfo.city}
                onChange={handleChange}
                style={styles.inputField}
                placeholder="City"
                required
              />
            </div>
            <div className="checkout-page-shipping-form-col">
              <select
                id="state"
                name="state"
                value={shippingInfo.state}
                onChange={handleChange}
                style={styles.inputField}
                required
              >
                <option value="" disabled>
                  Select State
                </option>
                {stateAbbreviations.map((state, index) => (
                  <option key={index} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
            <div className="checkout-page-shipping-form-col">
              <input
                type="text"
                id="zip_code"
                name="zip_code"
                value={shippingInfo.zip_code}
                onChange={handleChange}
                style={styles.inputField}
                placeholder="Zip Code"
                required
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

const styles = {
  formContainer: {
    padding: "1% 2.5%",
  },
  shippingHeader: {
    fontSize: "2vm",
    marginBottom: "1vm",
    fontWeight: "700",
  },
  shippingBox: {
    border: "2px solid #aaa",
    padding: "3%",
    marginTop: "1vh",
    marginBottom: "3%",
    borderRadius: "0",
    backgroundColor: "#ffffff",
  },
  inputField: {
    border: "2px solid #ccc",
    borderRadius: "0", // Sharp corners
    backgroundColor: "white", // White background
    outline: "none",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.05)",
  },
  shippingError: {
    color: "#FF0000",

    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};

export { ShippingAddressForm, validateShippingInfo, getFormattedInputAddress };
